import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import FilmCard from "@/components/base/FilmCard";
import Pagination from "@/components/base/Pagination";
import usePageMeta from "@/hooks/usePageMeta";
import { filterFilms, getGenres, getRegions, getYears, typeLabels, type SortKey } from "@/lib/films";
import type { FilmType } from "@/types/film";
import FilterPanel from "./components/FilterPanel";

const PAGE_SIZE = 6;

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "latest", label: "最新" },
  { key: "hot", label: "热门" },
  { key: "rating", label: "评分" },
];

const copy = {
  movie: {
    title: "电影",
    kicker: "Movie Channel",
    desc: "院线新片、影展佳作与类型片观察。按地区、年份与类型筛选，找到今晚想看的那一部。",
    keywords: "电影,最新电影,电影剧情介绍,电影影评,院线新片",
  },
  tv: {
    title: "电视剧",
    kicker: "Series Channel",
    desc: "热播剧集、口碑长剧与海外限定剧。按地区、年份与类型筛选，追踪正在更新的故事。",
    keywords: "电视剧,热播剧集,电视剧剧情介绍,剧集影评,追剧",
  },
};

export default function Category() {
  const params = useParams();
  const type: FilmType = params.type === "tv" ? "tv" : "movie";
  const meta = copy[type];

  const [region, setRegion] = useState("all");
  const [year, setYear] = useState("all");
  const [genre, setGenre] = useState("all");
  const [sort, setSort] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    setRegion("all");
    setYear("all");
    setGenre("all");
    setPage(1);
  }, [type]);

  usePageMeta({
    title: `${meta.title} - 人兽影视网`,
    description: `${meta.desc}人兽影视网${meta.title}频道收录最新${meta.title}资讯、剧情介绍与影评分析。`,
    keywords: meta.keywords,
  });

  const results = useMemo(
    () =>
      filterFilms({
        type,
        region: region === "all" ? undefined : region,
        year: year === "all" ? undefined : year,
        genre: genre === "all" ? undefined : genre,
        sort,
      }),
    [type, region, year, genre, sort],
  );

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = results.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const activeCount = [region, year, genre].filter((value) => value !== "all").length;

  const updateFilter = (key: "region" | "year" | "genre", value: string) => {
    if (key === "region") setRegion(value);
    if (key === "year") setYear(value);
    if (key === "genre") setGenre(value);
    setPage(1);
  };

  const resetFilters = () => {
    setRegion("all");
    setYear("all");
    setGenre("all");
    setPage(1);
  };

  const groups = [
    {
      key: "region" as const,
      label: "地区",
      options: getRegions(type).map((item) => ({ value: item, label: item })),
    },
    {
      key: "year" as const,
      label: "年份",
      options: getYears(type).map((item) => ({ value: String(item), label: `${item}` })),
    },
    {
      key: "genre" as const,
      label: "类型",
      options: getGenres(type).map((item) => ({ value: item, label: item })),
    },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="container-portal pt-6 md:pt-8">
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
          <Link to="/" className="transition-colors hover:text-primary-600 cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-500">分类</span>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-900">{meta.title}</span>
        </nav>

        <header className="mt-5 border-b border-background-200 pb-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <span className="label-caps text-[11px] text-accent-700">{meta.kicker}</span>
              <h1 className="mt-2 text-3xl font-black tracking-tight text-foreground-950 md:text-[38px]">
                {meta.title}
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-7 text-foreground-600">{meta.desc}</p>
            </div>
            <div className="flex items-center gap-4 text-xs text-foreground-500">
              <span>
                共 <strong className="font-label text-lg text-primary-700">{results.length}</strong> 部作品
              </span>
              <span className="h-8 w-px bg-background-300" />
              <span>
                当前第 <strong className="text-foreground-900">{currentPage}</strong> / {totalPages} 页
              </span>
            </div>
          </div>
        </header>

        <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:gap-7">
          <aside className="w-full shrink-0 lg:w-[240px]">
            <button
              type="button"
              onClick={() => setFiltersOpen((prev) => !prev)}
              className="flex w-full items-center justify-between rounded-lg border border-background-200 bg-background-50 px-4 py-3 text-sm font-medium text-foreground-800 lg:hidden cursor-pointer"
              aria-expanded={filtersOpen}
            >
              <span className="flex items-center gap-2">
                <i className="ri-filter-3-line text-accent-500" />
                筛选条件
                {activeCount > 0 && (
                  <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-500 px-1 text-[10px] font-semibold text-background-50">
                    {activeCount}
                  </span>
                )}
              </span>
              <i className={filtersOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
            </button>

            <div className={[filtersOpen ? "mt-3 block" : "hidden", "lg:block lg:mt-0"].join(" ")}>
              <FilterPanel
                groups={groups}
                values={{ region, year, genre }}
                onChange={updateFilter}
                onReset={resetFilters}
                activeCount={activeCount}
              />
            </div>
          </aside>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-100 px-3 py-2.5">
              <div className="flex items-center gap-1 overflow-x-auto">
                {sortOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => {
                      setSort(option.key);
                      setPage(1);
                    }}
                    className={[
                      "rounded-md px-3 py-1.5 text-xs transition-colors cursor-pointer whitespace-nowrap",
                      sort === option.key
                        ? "border border-background-300 bg-background-50 font-semibold text-primary-700"
                        : "text-foreground-600 hover:text-primary-600",
                    ].join(" ")}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-foreground-500">
                {typeLabels[type]} · 按{sortOptions.find((item) => item.key === sort)?.label}排序
              </p>
            </div>

            {pageItems.length > 0 ? (
              <div className="mt-5 grid grid-cols-2 gap-x-3 gap-y-7 sm:grid-cols-3 sm:gap-x-4">
                {pageItems.map((film) => (
                  <FilmCard key={film.id} film={film} />
                ))}
              </div>
            ) : (
              <div className="mt-5 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-film-line text-2xl" />
                </span>
                <h2 className="mt-4 text-base font-semibold text-foreground-900">
                  暂无符合筛选条件的影视作品
                </h2>
                <p className="mt-2 max-w-sm text-xs leading-6 text-foreground-500">
                  试着放宽地区或年份的限制，或者直接重置全部条件重新浏览。
                </p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-5 inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line" />
                  重置筛选条件
                </button>
              </div>
            )}

            {pageItems.length > 0 && (
              <Pagination current={currentPage} total={totalPages} onChange={setPage} />
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}